/** Runtime half of template seeding: Excalidraw text metrics + sticky fitting, ids/versions untouched. */

import { restoreElements } from '@excalidraw/excalidraw';
import type { OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import { centreIn, type El } from './elements';
import { isStickyContainer } from './sticky';
import { fitAllStatic } from './stickyFit';
import { buildTemplate, type TemplateId } from './templates';

async function fontsReady(families: string[]): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  const load = Promise.all(families.map((f) => document.fonts.load(`20px "${f}"`).catch(() => null))).then(() => document.fonts.ready);
  await Promise.race([load, new Promise((r) => setTimeout(r, 3000))]);
}

export async function buildSeed(template: TemplateId): Promise<OrderedExcalidrawElement[] | null> {
  const raw = buildTemplate(template);
  if (!raw.length) return null;
  await fontsReady(['Excalifont', 'Nunito']);
  let els = restoreElements(raw as never, null, { refreshDimensions: true, repairBindings: true }) as unknown as El[];
  els = fitAllStatic(els);
  const byId = new Map(els.map((e) => [e.id, e]));
  els = els.map((e) => {
    if (e.type !== 'text' || !e.containerId) return e;
    const c = byId.get(e.containerId);
    if (!c || isStickyContainer(c)) return e;
    return centreIn(e, c);
  });
  // Keep the builder's deterministic version / nonce (restore preserves them; be explicit).
  const stamps = new Map(raw.map((e) => [e.id, e]));
  return els.map((e) => ({ ...e, version: stamps.get(e.id)?.version ?? 1, versionNonce: stamps.get(e.id)?.versionNonce ?? 0 })) as unknown as OrderedExcalidrawElement[];
}
